import cookie from 'cookie';
import moment from 'moment';
import { log } from './debug_helper';
import SessionStorageHelper from './session_storage_helper';

import LocationHelper from './location_helper';
import Constants from './constants';
import FetchHelper from './fetch_helper';

const cookieDomain = LocationHelper.getCookieHost();

export default class AuthHelper {
  static OAUTH_TOKEN_KEY = 'oauthToken';
  static MFA_COOKIE_KEY = 'mfaSession';
  static GUEST_TOKEN_KEY = 'guestOauthToken';
  static IS_GUEST_SESSION_KEY = 'isGuestSession';
  static IS_PROPOSAL_COMPLETE_SESSION_KEY = 'isProposalComplete';
  static p2Token = 'PHPSESSID';
  static impersonateUserId = 'spruce-impersonate';
  static debugCookie = 'debugCookie';

  static deleteSession(userId) {
    this.setCookie(`${this.OAUTH_TOKEN_KEY}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;domain=${cookieDomain}`);

    let mfaCookieVal = cookie.parse(this.getCookie())?.[this.MFA_COOKIE_KEY];
    if (mfaCookieVal && userId) {
      let type = 'sms';
      FetchHelper.fetchJson(`users${userId}/mfas/${type}/sessions/${mfaCookieVal}`, {
        method: 'DELETE',
      });
    }

    this.setCookie(`${this.MFA_COOKIE_KEY}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;domain=${cookieDomain}`);
    this.deleteGuestToken();

    if (this.getSessionToken()) {
      log('a production cookie was possibly detected, cleaning up cookies...');
      this.setCookie(`${this.OAUTH_TOKEN_KEY}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;domain=.concertfin.net`);
    }
  }

  static impersonateNorman(userId, userName) {
    const impersonateCookie = btoa(`${userId}:${userName}`);
    this.setImpersonateCookie(impersonateCookie, 43200);
  }

  static setSessionCookie(token, expiresInSeconds) {
    const expiresDate = moment()
      .add(Number(expiresInSeconds) + 5, 'seconds')
      .toDate()
      .toUTCString();
    this.setCookie(`${this.OAUTH_TOKEN_KEY}=${token}; expires=${expiresDate}; path=/; domain=${cookieDomain};`);
  }

  static setImpersonateCookie(impersonateId, expiresInSeconds) {
    window.__aboutToImpersonate = true;
    setTimeout(() => {
      window.__aboutToImpersonate = false;
    }, 3000);
    const expiresDate = moment().add(1, 'day').toDate().toUTCString();
    this.setCookie(
      `${this.impersonateUserId}=${impersonateId}; expires=${expiresDate}; path=/; domain=${cookieDomain};`
    );
  }

  static setP2cookie(token, expiresInSeconds) {
    const expiresDate = moment()
      .add(Number(expiresInSeconds) + 5, 'seconds')
      .toDate()
      .toUTCString();
    this.setCookie(`${this.p2Token}=${token}; expires=${expiresDate}; path=/; domain=${cookieDomain};`);
  }

  static startGuestSession() {
    SessionStorageHelper.setDataInSession(this.IS_GUEST_SESSION_KEY, true);
  }

  static isGuestSession() {
    return !!SessionStorageHelper.getDataFromSession(this.IS_GUEST_SESSION_KEY);
  }

  static setGuestToken(token) {
    const expiresDate = moment()
      .add(Number(60 * 60) + 5, 'seconds')
      .toDate()
      .toUTCString();
    SessionStorageHelper.setDataInSession(this.GUEST_TOKEN_KEY, token, expiresDate);
    SessionStorageHelper.setDataInSession(this.IS_GUEST_SESSION_KEY, true, expiresDate);
  }

  static getGuestToken() {
    let guestToken = SessionStorageHelper.getDataFromSession(this.GUEST_TOKEN_KEY);
    return guestToken;
  }

  static extendGuestToken() {
    const guestToken = this.getGuestToken();
    if (guestToken) {
      this.setGuestToken(guestToken);
    }

    return guestToken;
  }

  static deleteGuestToken() {
    SessionStorageHelper.deleteDataFromSession(this.GUEST_TOKEN_KEY);
    SessionStorageHelper.deleteDataFromSession(this.IS_GUEST_SESSION_KEY);
  }

  static deleteP2Cookie() {
    this.setCookie(`${this.p2Token}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;domain=${cookieDomain}`);
  }

  static isShortAuth() {
    return LocationHelper.searchAsObj()?.auth;
  }

  static getSessionToken() {
    let sessionToken = cookie.parse(this.getCookie())[this.OAUTH_TOKEN_KEY];

    if (this.isShortAuth()) {
      sessionToken = `Short:${LocationHelper.searchAsObj()?.auth}`;
    }
    return sessionToken;
  }

  static getCognitoCookie() {
    let cognitoToken = cookie.parse(this.getCookie())['cognitotoken'];
    // console.log('cognitoToken', cognitoToken);
    return cognitoToken;
  }

  static getACookie(key) {
    return cookie.parse(this.getCookie())[key];
  }

  static setCookie(newCookie) {
    document.cookie = newCookie;
  }

  static setCompletedToken() {
    SessionStorageHelper.setDataInSession(this.IS_PROPOSAL_COMPLETE_SESSION_KEY, true);
  }

  static isCompletedToken() {
    return !!SessionStorageHelper.getDataFromSession(this.IS_PROPOSAL_COMPLETE_SESSION_KEY);
  }

  static getCookie() {
    return document.cookie;
  }

  static getImpersonateCookie() {
    const impersonationSession = sessionStorage.getItem('impersonation');
    if (impersonationSession) {
      return impersonationSession;
    }
    return cookie.parse(this.getCookie())[this.impersonateUserId];
  }

  static isUWImpersonating() {
    return this.getCognitoCookie() && this.getImpersonateCookie();
  }

  static deleteImpersonateCookie() {
    window.__aboutToImpersonate = true;
    setTimeout(() => {
      window.__aboutToImpersonate = false;
    }, 3000);
    this.setCookie(`${this.impersonateUserId}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;domain=${cookieDomain}`);
    return 'cookie';
  }

  static deleteImpersonateSessionToken() {
    window.__aboutToImpersonate = true;
    setTimeout(() => {
      window.__aboutToImpersonate = false;
    }, 3000);
    sessionStorage.removeItem('impersonation');
    return 'session';
  }

  static removeImpersonation() {
    if (sessionStorage.getItem('impersonation')) {
      return this.deleteImpersonateSessionToken();
    } else {
      return this.deleteImpersonateCookie();
    }
  }

  static isCPFUser(user) {
    return user?.atype === 'CPF';
  }
  static isAdminUser(user) {
    return user?.atype === 'A';
  }
  static isOpsMgrUser(user) {
    return user?.opsMgr;
  }

  static hasMosaicProduct(user) {
    const userProducts = user?.products;
    const evbSkus = Constants.LENDERS.filter((lender) => lender?.label?.includes('Mosaic')).map((o) => o?.productSku);
    return userProducts?.some(({ productSku }) => evbSkus.some((sku) => sku === productSku));
  }

  static hasEnerbankProduct(user) {
    const userProducts = user?.products;
    const evbSkus = Constants.LENDERS.filter((lender) => lender?.label?.includes('Enerbank')).map((o) => o?.productSku);
    return userProducts?.some(({ productSku }) => evbSkus.some((sku) => sku === productSku));
  }

  static hasEverbrightProduct(user) {
    const userProducts = user?.products;
    const evbSkus = Constants.LENDERS.filter((lender) => lender?.label?.includes('EverBright')).map(
      (o) => o?.productSku
    );
    return userProducts?.some(({ productSku }) => evbSkus.some((sku) => sku === productSku));
  }

  static hasProduct(user, sku) {
    return user?.products?.filter((e) => e.productSku.includes(sku))?.length > 0;
  }

  static welcomeCallTokenAuth() {
    let jwt = sessionStorage.getItem('WC_JWT_Token');
    if (jwt) {
      return `Bearer ${jwt}`;
    }
    if (AuthHelper.getSessionToken()) {
      return `Bearer ${AuthHelper.getSessionToken()}`;
    }
    return `Bearer asdf`;
  }
}
